<template>
    <popup-modal
        :title="title"
        @close="close"
    >
        <div>
            <component :form="form"
                :is="specificComponentConfigurator">
            </component>
        </div>

        <div class="w-full flex items-center justify-between pt-32">
            <component-identifier v-model="form.identifier"></component-identifier>

            <simple-button
                display="secondary"
                :disabled="!form.isDirty()"
                :loading="savingComponent"
                look="primary"
                @click="handleSubmit"
            >
                {{ $t('general.save') }}
            </simple-button>
        </div>
    </popup-modal>
</template>

<script>
import PopupModal from './PopupModal.vue';
import SvgIcon from './SvgIcon.vue';
import ConfigureAwards from './ConfigureAwards.vue';
import ConfigureFacebook from './ConfigureFacebook.vue';
import ConfigureFacts from './ConfigureFacts.vue';
import ConfigureGallery from './ConfigureGallery.vue';
import ConfigureImage from './ConfigureImage.vue';
import ConfigureJobs from './ConfigureJobs.vue';
import ConfigureLocations from './ConfigureLocations.vue';
import ConfigurePerks from './ConfigurePerks.vue';
import ConfigureQuotes from './ConfigureQuotes.vue';
import ConfigureTeam from './ConfigureTeam.vue';
import ConfigureText from './ConfigureText.vue';
import ConfigureTwitter from './ConfigureTwitter.vue';
import ConfigureValues from './ConfigureValues.vue';
import ConfigureVideo from './ConfigureVideo.vue';
import SimpleButton from '../ui/SimpleButton.vue';
import * as components from '../../services/ComponentService';
import Form from '../../etc/forms/Form';
import Component from '../../models/Component';
import { normalizeJson } from '../../../shared/helpers';
import {mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useBrandingStore} from '../../../shared/state/branding';
import database from '../../state/database';
import ComponentIdentifier from './ComponentIdentifier';
import ComponentAward from "../../models/ComponentAward";
import ComponentFacebook from "../../models/ComponentFacebook";
import ComponentFact from "../../models/ComponentFact";
import ComponentImage from "../../models/ComponentImage";
import ComponentJob from "../../models/ComponentJob";
import ComponentLocation from "../../models/ComponentLocation";
import ComponentMember from "../../models/ComponentMember";
import ComponentPerk from "../../models/ComponentPerk";
import ComponentQuote from "../../models/ComponentQuote";
import ComponentTwitter from "../../models/ComponentTwitter";
import ComponentValue from "../../models/ComponentValue";
import ComponentVideo from "../../models/ComponentVideo";


export default {
    components: {
        ComponentIdentifier,
        'popup-modal': PopupModal,
        'svg-icon': SvgIcon,
        'configure-awards': ConfigureAwards,
        'configure-facebook': ConfigureFacebook,
        'configure-facts': ConfigureFacts,
        'configure-gallery': ConfigureGallery,
        'configure-image': ConfigureImage,
        'configure-jobs': ConfigureJobs,
        'configure-locations': ConfigureLocations,
        'configure-perks': ConfigurePerks,
        'configure-quotes': ConfigureQuotes,
        'configure-team': ConfigureTeam,
        'configure-text': ConfigureText,
        'configure-twitter': ConfigureTwitter,
        'configure-values': ConfigureValues,
        'configure-video': ConfigureVideo,
        'simple-button': SimpleButton,
    },
    props: {
        component: Object,
    },
    data: function () {
        return {
            activeTab: 'component-content',
            selectedItems: [],
            something: '',
            test: true,
            form: this.generateForm(),
            savingComponent: false,
            entityMap: {
                component_awards: ComponentAward,
                component_facebooks: ComponentFacebook,
                component_facts: ComponentFact,
                component_images: ComponentImage,
                component_jobs: ComponentJob,
                component_locations: ComponentLocation,
                component_members: ComponentMember,
                component_perks: ComponentPerk,
                component_quotes: ComponentQuote,
                component_twitters: ComponentTwitter,
                component_values: ComponentValue,
                component_videos: ComponentVideo,
            }
        }
    },
    methods: {
        ...mapActions(useBrandingStore, [ 'stopConfiguringComponent' ]),
        generateForm () {
            let formData = Component.formFields(this.component.type);

            if (this.component.id !== 'undefined') {
                this.hydrateFormData(formData);
            }
            return new Form(formData);
        },
        hydrateFormData (formData) {
            formData.forEach((field, index) => {
                formData[index].value = this.component[field.name] || field.value;
            });
        },
        close () {
            this.$emit('close');
        },
        addIt () {
            this.selectedItems.push(this.something);
        },
        setTab (tab) {
            this.activeTab = tab;
        },
        async handleSubmit () {
            this.savingComponent = true;

            if (this.exists) {
                await this.saveExistingComponent();
            } else {
                await this.saveNewComponent();
            }

            this.savingComponent = false;
            this.stopConfiguringComponent();
        },
        saveNewComponent () {
            return this.form.submit(data => components.store({
                data: Component.formatBeforeSave(this.form.data, this.component)
            })).then(result => {
                let resultData = normalizeJson(result);

                const components = useRepo(Component)
                                    .where((c) => {
                                        return c.company_id == resultData.company_id && c.order >= resultData.order;
                                    })
                                    .get();

                for (let i = 0; i < components.length; i++) {
                    useRepo(Component)
                        .whereId(components[i].id)
                        .update({order: components[i].order + 1});
                }

                return useRepo(Component).save(resultData);
            });
        },
        saveExistingComponent () {
            return this.form.submit(data => components.update(this.component.id, {
                data: Component.formatBeforeSave(this.form.data, this.component)
            })).then(result => {
                //pick up current component.rerenderCount Value
                //with Pinia ORM for reactivity purpose
                result.attributes.rerenderCount = useRepo(Component).find(result.id).rerenderCount + 1;

                Object.keys(result.relationships || {}).forEach(relation => {

                    let model = 'component_' + relation;

                    Object.values(useRepo(this.entityMap[model]).get())
                        .filter(entry => entry.component_id === result.id)
                        .forEach(entry => {
                            useRepo(this.entityMap[model])
                                .where((record) => {
                                    const pk = entry.$primaryKey();
                                    return (typeof pk === "string")
                                        ? record[pk] == entry[pk]
                                        : pk.every(k => record[k] == entry[k]);
                                }).delete();
                        });
                });

                return useRepo(Component).save(normalizeJson(result));
            });
        }
    },
    computed: {
        exists () {
            return typeof this.component.id !== 'undefined';
        },
        title () {
            return this.$t('site_editor.' + (this.exists ? 'edit' : 'add') + '_block',
                {block: this.$t('site_editor.' + this.component.type + '.title')});
        },
        specificComponentConfigurator () {
            return 'configure-' + this.component.type;
        }
    },
    created () {
        if (this.component.type === 'gallery' &&
            (typeof this.component.settings != 'undefined') &&
            (this.component.settings !== null) &&
            this.component.settings.hasOwnProperty('single') &&
            this.component.settings.single) {
            this.component.type = 'image';
        }
    }
}
</script>
